import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';

import useAuth from '../../auth/useAuth';
import servicesApi from '../../api/servicesApi';
import serviceOrdersApi from '../../api/serviceOrdersApi';
import { ROLES } from '../../config';
import { getErrorMessage } from '../../utils/errorHandler';
import { SERVICE_ORDER_STATUS_COLORS, serviceOrderStatusLabel } from '../../utils/serviceOrderStatuses';

const formatMoney = (value) => new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(value || 0);

// Reglas del adjunto de fotos: hasta 3, formatos comunes de camara de celular (heic/heif de
// iPhone incluidos) y 3MB como maximo por archivo.
const MAX_IMAGES = 3;
const MAX_IMAGE_SIZE = 3 * 1024 * 1024;
const ALLOWED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'heic', 'heif'];

export default function ServiceRequestPage() {
  const { user, isAuthenticated } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedService, setSelectedService] = useState(null);

  const [description, setDescription] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [images, setImages] = useState([]);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  // Telefono obligatorio para invitado y cuenta registrada (con cuenta se precarga el suyo).
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState(null);

  // Solo el cliente (con cuenta o invitado) puede solicitar un servicio desde aqui;
  // administrador/tecnico/distribuidor tienen sus propias areas (ver Header.js).
  const isBlockedRole = isAuthenticated && user?.role && user.role !== ROLES.CLIENTE;

  // Vistas previas de las fotos elegidas; se liberan cada vez que la lista cambia o al desmontar.
  const imagePreviews = useMemo(() => images.map((file) => URL.createObjectURL(file)), [images]);
  useEffect(() => () => imagePreviews.forEach((url) => URL.revokeObjectURL(url)), [imagePreviews]);

  useEffect(() => {
    if (user?.phone) {
      setPhone(user.phone);
    }
  }, [user]);

  useEffect(() => {
    const loadServices = async () => {
      setLoading(true);
      try {
        const { data } = await servicesApi.getCatalog();
        setServices(data.data);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudieron cargar los servicios.'), { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadServices();
  }, [enqueueSnackbar]);

  const serviceTypes = useMemo(() => {
    const unique = new Map();
    services.forEach((service) => {
      if (service.service_type) {
        unique.set(service.service_type.id, service.service_type.name);
      }
    });
    return Array.from(unique, ([id, name]) => ({ id, name }));
  }, [services]);

  const filteredServices = typeFilter
    ? services.filter((service) => service.service_type?.id === typeFilter)
    : services;

  const handleSelect = (service) => {
    setSelectedService(service);
    setDescription('');
  };

  const handleImagesChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ''; // permite volver a elegir el mismo archivo si se quito antes

    const accepted = [];
    for (const file of files) {
      if (images.length + accepted.length >= MAX_IMAGES) {
        enqueueSnackbar(`Puedes adjuntar hasta ${MAX_IMAGES} fotos.`, { variant: 'warning' });
        break;
      }

      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_IMAGE_EXTENSIONS.includes(extension)) {
        enqueueSnackbar(`"${file.name}" no es un formato permitido (png, jpg, jpeg, heic).`, { variant: 'warning' });
        continue;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        enqueueSnackbar(`"${file.name}" supera el peso máximo de 3MB.`, { variant: 'warning' });
        continue;
      }

      accepted.push(file);
    }

    if (accepted.length > 0) {
      setImages((prev) => [...prev, ...accepted]);
    }
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedService) {
      enqueueSnackbar('Selecciona un servicio.', { variant: 'warning' });
      return;
    }
    if (!description.trim()) {
      enqueueSnackbar('Cuéntanos qué le pasa a tu equipo.', { variant: 'warning' });
      return;
    }
    if (!isAuthenticated && !guestName.trim()) {
      enqueueSnackbar('Ingresa tu nombre para continuar como invitado.', { variant: 'warning' });
      return;
    }
    if (!phone.trim()) {
      enqueueSnackbar('Ingresa un número de teléfono para coordinar contigo.', { variant: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('service_id', selectedService.id);
      formData.append('description', description);
      formData.append('is_urgent', isUrgent ? '1' : '0');
      formData.append('customer_phone', phone.trim());
      if (!isAuthenticated) {
        formData.append('customer_name', guestName);
        if (guestEmail.trim()) {
          formData.append('customer_email', guestEmail.trim());
        }
      }
      images.forEach((image) => formData.append('images[]', image));

      const { data } = await serviceOrdersApi.requestService(formData);
      setSubmittedOrder(data.data);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo registrar tu solicitud.'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (isBlockedRole) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="info">
          El servicio técnico solo puede solicitarse desde una cuenta de cliente. Cierra sesión e ingresa con una
          cuenta de cliente, o continúa como invitado.
        </Alert>
      </Container>
    );
  }

  if (submittedOrder) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent>
            <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center', py: 3 }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56 }} />
              <Typography variant="h5">¡Solicitud registrada!</Typography>
              <Typography color="text.secondary">
                Orden N.° {submittedOrder.id} · {submittedOrder.service?.name}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={serviceOrderStatusLabel(submittedOrder.status?.name || 'pendiente')}
                  color={SERVICE_ORDER_STATUS_COLORS[submittedOrder.status?.name || 'pendiente']}
                />
                {submittedOrder.is_urgent && <Chip label="Respuesta inmediata" color="warning" />}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Nos pondremos en contacto contigo por WhatsApp para coordinar la recepción de tu equipo.
              </Typography>
              {isAuthenticated && (
                <Button variant="outlined" component={RouterLink} to="/mis-ordenes-servicio">
                  Ver seguimiento de mis órdenes
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => {
                  setSubmittedOrder(null);
                  setSelectedService(null);
                  setIsUrgent(false);
                  setImages([]);
                }}
              >
                Solicitar otro servicio
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Stack spacing={1} sx={{ mb: 4 }}>
        <Typography variant="h4">Servicio técnico</Typography>
        <Typography color="text.secondary">
          Elige el servicio que necesitas (mantenimiento, reparación, soporte técnico o instalación de Windows) y
          solicítalo. Te contactaremos para coordinar la recepción de tu equipo.
        </Typography>
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label="Todos"
              color={typeFilter === '' ? 'primary' : 'default'}
              onClick={() => setTypeFilter('')}
            />
            {serviceTypes.map((type) => (
              <Chip
                key={type.id}
                label={type.name}
                color={typeFilter === type.id ? 'primary' : 'default'}
                onClick={() => setTypeFilter(type.id)}
              />
            ))}
          </Stack>

          {!loading && filteredServices.length === 0 && (
            <Alert severity="info">No hay servicios disponibles en esta categoría por ahora.</Alert>
          )}

          <Grid container spacing={2}>
            {loading
              ? Array.from({ length: 4 }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid key={index} size={{ xs: 12, sm: 6 }}>
                    <Skeleton variant="rounded" height={140} />
                  </Grid>
                ))
              : filteredServices.map((service) => (
                  <Grid key={service.id} size={{ xs: 12, sm: 6 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderColor: selectedService?.id === service.id ? 'primary.main' : undefined,
                        borderWidth: selectedService?.id === service.id ? 2 : 1,
                      }}
                    >
                      <CardActionArea onClick={() => handleSelect(service)}>
                        <CardContent>
                          <Stack spacing={1}>
                            <Chip
                              size="small"
                              icon={<BuildOutlinedIcon fontSize="small" />}
                              label={service.service_type?.name}
                              sx={{ alignSelf: 'flex-start' }}
                            />
                            <Typography variant="subtitle1">{service.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {service.description}
                            </Typography>
                            <Typography variant="h6">{formatMoney(service.price)}</Typography>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
          </Grid>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ position: 'sticky', top: 88 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tu solicitud
              </Typography>

              {!selectedService ? (
                <Alert severity="info">Selecciona un servicio para continuar.</Alert>
              ) : (
                <Stack spacing={2.5}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2">{selectedService.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatMoney(selectedService.price)} · {selectedService.service_type?.name}
                    </Typography>
                  </Stack>

                  <TextField
                    label="Cuéntanos qué le pasa a tu equipo"
                    fullWidth
                    multiline
                    minRows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />

                  <FormControlLabel
                    control={<Checkbox checked={isUrgent} onChange={(e) => setIsUrgent(e.target.checked)} />}
                    label="Solicitar técnico (respuesta inmediata 5 - 15min)"
                  />

                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Fotos (opcional, máximo {MAX_IMAGES})</Typography>

                    {images.length > 0 && (
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {images.map((image, index) => (
                          <Box
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            sx={{ position: 'relative', width: 72, height: 72, borderRadius: 1, overflow: 'hidden' }}
                          >
                            <Box
                              component="img"
                              src={imagePreviews[index]}
                              alt={`Adjunto ${index + 1}`}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => removeImage(index)}
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                color: 'common.white',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        ))}
                      </Stack>
                    )}

                    {images.length < MAX_IMAGES && (
                      <Button
                        component="label"
                        variant="outlined"
                        size="small"
                        startIcon={<AddPhotoAlternateOutlinedIcon />}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Agregar foto
                        <input
                          type="file"
                          hidden
                          multiple
                          accept="image/png,image/jpeg,.heic,.heif"
                          onChange={handleImagesChange}
                        />
                      </Button>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Formatos png, jpg, jpeg o heic (fotos de celular). Hasta 3MB por foto.
                    </Typography>
                  </Stack>

                  {isAuthenticated ? (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Solicitando como <strong>{user?.name}</strong> ({user?.email})
                      </Typography>
                      <TextField
                        label="Teléfono"
                        fullWidth
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        helperText="Te llamaremos o escribiremos a este número para coordinar."
                      />
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Solicitando como invitado.{' '}
                        <Link component={RouterLink} to="/login" state={{ from: location }}>
                          ¿Ya tienes cuenta? Inicia sesión
                        </Link>
                      </Typography>
                      <TextField
                        label="Nombre completo"
                        fullWidth
                        required
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                      />
                      <TextField
                        label="Teléfono"
                        fullWidth
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                      <TextField
                        label="Correo electrónico (opcional)"
                        type="email"
                        fullWidth
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                      />
                    </>
                  )}

                  <Button variant="contained" size="large" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? 'Enviando...' : 'Solicitar servicio'}
                  </Button>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
